import { Link, Stack } from '@mui/joy';
import { CellClickedEvent, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useState } from 'react';

import { IElement } from 'bom/types';
import { Loader, TextLine } from 'core/elements';
import { NumberHelper } from 'core/helpers';
import { useAgGridCellUpdateContext } from 'core/states';
import { getAgGridTableCellData } from 'core/utils';
import { ManageElements } from 'jobs/components/JobDetails';
import { IJobPart } from 'jobs/types';
import { theme } from 'theme';
import { Size } from 'theme/enums';

interface IProps extends ICellRendererParams {
  isEditable: (data: IJobPart) => boolean;
  element?: IElement;
}

export const QuantityCell = ({ isEditable, element, ...props }: IProps) => {
  const [isOpenManageElements, setIsOpenManageElements] = useState(false);

  const { isUpdating } = useAgGridCellUpdateContext();

  const data = getAgGridTableCellData(props, 'computedCostQuantity');

  const isRowEditable = isEditable(data);

  useEffect(() => {
    const handleClick = (eventParams: CellClickedEvent) => {
      if (
        eventParams.node.rowIndex === props.node.rowIndex &&
        eventParams.column.getColId() === props.column.getColId()
      ) {
        setIsOpenManageElements(true);
      }
    };

    props.api.addEventListener('cellClicked', handleClick);

    return () => {
      !props.api.isDestroyed() && props.api.removeEventListener('cellClicked', handleClick);
    };
  }, [props.api, props.column, props.node.rowIndex]);

  if (!data) {
    return NumberHelper.format({ amount: props?.value });
  }

  const isUpdatingValue = isUpdating(props.node.id, 'computedCostQuantity');

  if (isUpdatingValue) {
    return <Loader size={Size.SM} style={{ justifyContent: 'flex-end', margin: 0 }} />;
  }

  const { quantityInCostUom, computedCostQuantity, quantitiesPerElement } = props?.data || {};

  if (element?.id) {
    return <TextLine text={NumberHelper.format({ amount: quantitiesPerElement?.[element?.id] })} />;
  }

  const shouldDisplayQuantityInCostUom = computedCostQuantity !== quantityInCostUom;

  return (
    <Stack
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      {getQuantityContent(isRowEditable, computedCostQuantity)}

      {shouldDisplayQuantityInCostUom && (
        <TextLine text={NumberHelper.format({ amount: quantityInCostUom })} isSubText />
      )}

      {isRowEditable && (
        <ManageElements
          isOpen={isOpenManageElements}
          onClose={() => setIsOpenManageElements(false)}
          bomPart={data}
        />
      )}
    </Stack>
  );
};

const getQuantityContent = (isRowEditable: boolean, computedCostQuantity: number) => {
  const formattedQuantity = NumberHelper.format({ amount: computedCostQuantity });

  if (!isRowEditable) {
    return <TextLine text={formattedQuantity} />;
  }

  return (
    <Link
      color={null}
      sx={{
        textWrap: 'wrap',
        '&:hover': { color: theme.palette.primary.softColor },
      }}
    >
      {formattedQuantity}
    </Link>
  );
};
